import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
  Paper,
  Fade,
  Zoom,
  Avatar,
} from "@mui/material";
import {
  Close,
  NavigateNext,
  NavigateBefore,
  SmartToy,
  PlayArrow,
  Pause,
  Replay,
  CheckCircle,
  FiberManualRecord,
  Spellcheck,
  Upload,
  Mic,
  ContentCopy,
  Download,
  Delete,
  Translate,
  Search,
  StickyNote2,
  FormatQuote,
  AutoAwesome,
  Edit,
  PlagiarismOutlined,
  KeyboardArrowRight,
} from "@mui/icons-material";

// ─── Tour Steps ───────────────────────────────────────────────────────────────
const TOUR_STEPS = [
  {
    id: "welcome",
    title: "Welcome to CoAuthor! 🎉",
    description:
      "Hi! I'm your CoAuthor guide bot. I'll walk you through every feature on this screen so your entire team can get up to speed fast. Let's begin the tour!",
    icon: <SmartToy />,
    color: "#6366f1",
    highlight: null,
    category: "intro",
  },
  {
    id: "doc-name",
    title: "Document Name Bar",
    description:
      "At the top you'll find the document title field. Click to rename your document. It auto-saves whenever you stop typing — no need to hit a Save button manually.",
    icon: <Edit />,
    color: "#0ea5e9",
    highlight: "tour-doc-name",
    category: "editor",
  },
  {
    id: "editor-area",
    title: "Rich Text Editor",
    description:
      "This is the main writing canvas. It supports rich formatting — headings, bold, italic, lists, images, and more. You can also drag-and-drop .docx or .txt files directly here.",
    icon: <Edit />,
    color: "#10b981",
    highlight: "tour-editor-area",
    category: "editor",
  },
  {
    id: "word-count",
    title: "Word Count Tracker",
    description:
      "Live word count shown at the bottom toolbar. The limit is 2,500 words per document. The counter updates in real-time as you type.",
    icon: <FiberManualRecord />,
    color: "#1976D2",
    highlight: "tour-word-count",
    category: "editor",
  },
  {
    id: "paraphrase",
    title: "Paraphrase Tool",
    description:
      "Rewrites your content in a fresh way while preserving meaning. Great for avoiding repetition or refreshing existing text. Opens a panel at the bottom of the screen.",
    icon: <AutoAwesome />,
    color: "#8b5cf6",
    highlight: "tour-paraphrase-btn",
    category: "ai-tools",
  },
  {
    id: "summarize",
    title: "Summarize Tool",
    description:
      "Condenses long documents into concise summaries. The AI extracts key points and presents them in a clean, readable format in the lower panel.",
    icon: <AutoAwesome />,
    color: "#f59e0b",
    highlight: "tour-summarize-btn",
    category: "ai-tools",
  },
  {
    id: "ocr",
    title: "OCR (Image to Text)",
    description:
      "Have images with text in your document? Click OCR and the AI extracts all readable text from your images and inserts it below each image automatically.",
    icon: <Search />,
    color: "#06b6d4",
    highlight: "tour-ocr-btn",
    category: "ai-tools",
  },
  {
    id: "grammar-check",
    title: "Grammar Check",
    description:
      "Runs a full grammar & fluency analysis on your text. Suggestions appear in the right panel — you can Accept All, Ignore All, or handle each suggestion one by one.",
    icon: <Spellcheck />,
    color: "#ef4444",
    highlight: "tour-grammar-btn",
    category: "ai-tools",
  },
  {
    id: "delete",
    title: "Delete / Clear Editor",
    description:
      "Clears all content from the editor in one click. Also resets any grammar suggestions and transcribed speech. Use with caution — this action cannot be undone!",
    icon: <Delete />,
    color: "#dc2626",
    highlight: "tour-delete-btn",
    category: "toolbar",
  },
  {
    id: "upload",
    title: "Upload Document",
    description:
      "Upload .docx or .txt files directly into the editor. DOCX files preserve images and rich formatting via mammoth.js conversion. You can also drag-and-drop onto the editor.",
    icon: <Upload />,
    color: "#0369a1",
    highlight: "tour-upload-btn",
    category: "toolbar",
  },
  {
    id: "download",
    title: "Download Document",
    description:
      "Download your work as a .docx Word file. The download button appears after the document has been saved at least once (when a document ID exists).",
    icon: <Download />,
    color: "#059669",
    highlight: "tour-download-btn",
    category: "toolbar",
  },
  {
    id: "copy",
    title: "Copy to Clipboard",
    description:
      "Copies all plain text from the editor to your clipboard instantly. Useful for pasting into emails, Slack messages, or any other tool.",
    icon: <ContentCopy />,
    color: "#1d4ed8",
    highlight: "tour-copy-btn",
    category: "toolbar",
  },
  {
    id: "translate",
    title: "Translate",
    description:
      "Opens the translation panel at the bottom. Select your target language and the AI translates your entire document. Supports 50+ languages.",
    icon: <Translate />,
    color: "#7c3aed",
    highlight: "tour-translate-btn",
    category: "toolbar",
  },
  {
    id: "mic",
    title: "Voice Dictation (Mic)",
    description:
      "Click the mic to start speaking — your speech is transcribed in real-time and appended to the editor. Click again to stop. Requires an active subscription plan.",
    icon: <Mic />,
    color: "#db2777",
    highlight: "tour-mic-btn",
    category: "toolbar",
  },
  {
    id: "chevron",
    title: "Quick Tools Panel (🪄 Magic Wand)",
    description:
      "The purple wand button on the right edge opens the Quick Tools drawer. From here you can access Search, Notes, Citations, AI Review, and Plagiarism without switching views.",
    icon: <AutoAwesome />,
    color: "#667eea",
    highlight: "tour-chevron-btn",
    category: "sidebar",
  },
  {
    id: "search-tool",
    title: "Search (Research Tool)",
    description:
      "Search the web for research sources directly inside the editor. Results appear in the right panel and you can insert author names or quotes directly into your document.",
    icon: <Search />,
    color: "#9c27b0",
    highlight: "tour-search-tool",
    category: "sidebar",
  },
  {
    id: "notes-tool",
    title: "Notes",
    description:
      "Keep personal research notes alongside your document. Notes are saved separately and won't appear in your published document. Perfect for outlines and reminders.",
    icon: <StickyNote2 />,
    color: "#ff6f00",
    highlight: "tour-notes-tool",
    category: "sidebar",
  },
  {
    id: "citation-tool",
    title: "Citations",
    description:
      "Generate properly formatted citations (APA, MLA, Chicago) for your sources. Search by title, URL, or DOI and insert formatted citations instantly.",
    icon: <FormatQuote />,
    color: "#e91e63",
    highlight: "tour-citation-tool",
    category: "sidebar",
  },
  {
    id: "aireview-tool",
    title: "AI Review",
    description:
      "Comprehensive AI-powered document review covering tone, clarity, structure, grammar, and style. Provides actionable suggestions you can apply with one click.",
    icon: <CheckCircle />,
    color: "#4caf50",
    highlight: "tour-aireview-tool",
    category: "sidebar",
  },
  {
    id: "plagiarism-tool",
    title: "Plagiarism Checker",
    description:
      "Scans your document against billions of web pages and academic sources. Results show a match percentage and source links so you can make targeted corrections.",
    icon: <PlagiarismOutlined />,
    color: "#f44336",
    highlight: "tour-plagiarism-tool",
    category: "sidebar",
  },
  {
    id: "autosave",
    title: "Auto-Save & Review Workflow",
    description:
      "Your document auto-saves 2 seconds after you stop typing. If a document is shared with you, a 'Submit for Review' button appears at the top-right for team collaboration.",
    icon: <CheckCircle />,
    color: "#059669",
    highlight: null,
    category: "editor",
  },
  {
    id: "done",
    title: "You're all set! 🚀",
    description:
      "That's the full CoAuthor feature tour! Your team now knows every tool available. You can relaunch this tour anytime by clicking the bot button. Happy writing!",
    icon: <SmartToy />,
    color: "#6366f1",
    highlight: null,
    category: "intro",
  },
];

const CATEGORY_META = {
  intro: { label: "Intro", color: "#6366f1" },
  editor: { label: "Editor", color: "#10b981" },
  "ai-tools": { label: "AI Tools", color: "#f59e0b" },
  toolbar: { label: "Toolbar", color: "#0ea5e9" },
  sidebar: { label: "Sidebar", color: "#e91e63" },
};

// ─── Bot Avatar ───────────────────────────────────────────────────────────────
function BotAvatar({ color, isTalking }) {
  return (
    <Box sx={{ position: "relative", width: 52, height: 52, flexShrink: 0 }}>
      <Box
        sx={{
          position: "absolute",
          inset: -4,
          borderRadius: "50%",
          border: `2px solid ${color}`,
          animation: "pulse-ring 1.8s ease-out infinite",
          "@keyframes pulse-ring": {
            "0%": { transform: "scale(1)", opacity: 0.7 },
            "100%": { transform: "scale(1.55)", opacity: 0 },
          },
        }}
      />
      <Avatar
        sx={{
          width: 52,
          height: 52,
          background: `linear-gradient(135deg, ${color}, ${color}99)`,
          border: `2px solid ${color}`,
          boxShadow: `0 0 18px ${color}55`,
          animation: isTalking
            ? "bob 0.55s ease-in-out infinite alternate"
            : "floatbot 3s ease-in-out infinite",
          "@keyframes bob": {
            "0%": { transform: "translateY(0) scale(1)" },
            "100%": { transform: "translateY(-5px) scale(1.06)" },
          },
          "@keyframes floatbot": {
            "0%,100%": { transform: "translateY(0)" },
            "50%": { transform: "translateY(-7px)" },
          },
        }}
      >
        <SmartToy sx={{ fontSize: 28, color: "#fff" }} />
      </Avatar>
      {isTalking && (
        <Box
          sx={{
            position: "absolute",
            bottom: -10,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: "3px",
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                bgcolor: color,
                animation: `dot-bounce 0.8s ease-in-out ${i * 0.16}s infinite`,
                "@keyframes dot-bounce": {
                  "0%,100%": { transform: "translateY(0)" },
                  "50%": { transform: "translateY(-6px)" },
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Spotlight ────────────────────────────────────────────────────────────────
function SpotlightOverlay({ targetId, color }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!targetId) { setRect(null); return; }
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      // small delay to let scroll settle
      setTimeout(() => {
        const r = el.getBoundingClientRect();
        setRect({ ...r });
      }, 300);
    }
  }, [targetId]);

  if (!rect) return null;
  const PAD = 10;
  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1299, pointerEvents: "none" }}>
      <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
        <defs>
          <mask id="spm">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={rect.left - PAD} y={rect.top - PAD}
              width={rect.width + PAD * 2} height={rect.height + PAD * 2}
              rx="10" fill="black"
            />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.52)" mask="url(#spm)" />
        <rect
          x={rect.left - PAD} y={rect.top - PAD}
          width={rect.width + PAD * 2} height={rect.height + PAD * 2}
          rx="10" fill="none" stroke={color} strokeWidth="2.5"
          style={{ filter: `drop-shadow(0 0 10px ${color})` }}
        />
      </svg>
    </Box>
  );
}

// ─── Step Mini-Map ────────────────────────────────────────────────────────────
function MiniMap({ steps, currentIdx, onJump }) {
  const cats = [...new Set(steps.map((s) => s.category))];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, mt: 1 }}>
      {cats.map((cat) => {
        const meta = CATEGORY_META[cat];
        return (
          <Box key={cat} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Typography sx={{ fontSize: 8, fontWeight: 800, color: meta.color, textTransform: "uppercase", letterSpacing: "0.07em", minWidth: 44 }}>
              {meta.label}
            </Typography>
            <Box sx={{ display: "flex", gap: "3px", flexWrap: "wrap" }}>
              {steps.filter((s) => s.category === cat).map((step) => {
                const idx = steps.indexOf(step);
                const done = idx < currentIdx;
                const active = idx === currentIdx;
                return (
                  <Tooltip key={step.id} title={step.title} placement="top">
                    <Box
                      onClick={() => onJump(idx)}
                      sx={{
                        width: active ? 18 : 7,
                        height: 7,
                        borderRadius: 4,
                        bgcolor: active ? meta.color : done ? `${meta.color}77` : `${meta.color}22`,
                        cursor: "pointer",
                        transition: "all 0.3s",
                        "&:hover": { transform: "scale(1.4)" },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Main TourBot Export ──────────────────────────────────────────────────────
export default function CoAuthorTourBot({
  autoStart = false,
  onAutoStart,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [talking, setTalking] = useState(false);
  const [displayed, setDisplayed] = useState("");
  const [typing, setTyping] = useState(false);
  const typeTimer = useRef(null);
  const playTimer = useRef(null);

  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;
  const isFirst = step === 0;
  const progress = ((step + 1) / TOUR_STEPS.length) * 100;

  const typeText = useCallback((text) => {
    setDisplayed("");
    setTyping(true);
    setTalking(true);
    let i = 0;
    clearInterval(typeTimer.current);
    typeTimer.current = setInterval(() => {
      if (i <= text.length) {
        setDisplayed(text.slice(0, i));
        i++;
      } else {
        clearInterval(typeTimer.current);
        setTyping(false);
        setTalking(false);
      }
    }, 16);
  }, []);

  useEffect(() => {
    if (isOpen) typeText(current.description);
    return () => clearInterval(typeTimer.current);
  }, [step, isOpen]);

  useEffect(() => {
    if (playing && !typing) {
      playTimer.current = setTimeout(() => {
        if (step < TOUR_STEPS.length - 1) setStep((p) => p + 1);
        else setPlaying(false);
      }, 3800);
    }
    return () => clearTimeout(playTimer.current);
  }, [playing, step, typing]);

  const next = () => { if (!isLast) setStep((p) => p + 1); };
  const prev = () => { if (!isFirst) setStep((p) => p - 1); };
  const restart = () => { setStep(0); setPlaying(false); };
  const close = () => { setIsOpen(false); setPlaying(false); setStep(0); };

  useEffect(() => {
    if (autoStart && !isOpen) {
      setIsOpen(true);
      if (typeof onAutoStart === "function") {
        onAutoStart();
      }
    }
  }, [autoStart, isOpen, onAutoStart]);

  return (
    <>
      {/* Spotlight */}
      {isOpen && <SpotlightOverlay targetId={current.highlight} color={current.color} />}

      {/* Launch Button */}
      {!isOpen && (
        <Zoom in>
          <Box sx={{ position: "fixed", bottom: 95, right: 80, zIndex: 1400 }}>
            <Tooltip title="Start Feature Tour" placement="left">
              <Box
                onClick={() => setIsOpen(true)}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 4px 20px rgba(99,102,241,0.55)",
                  animation: "floatbtn 3s ease-in-out infinite",
                  "@keyframes floatbtn": {
                    "0%,100%": { transform: "translateY(0)" },
                    "50%": { transform: "translateY(-8px)" },
                  },
                  "&:hover": { transform: "scale(1.1)", transition: "transform 0.2s" },
                }}
              >
                <SmartToy sx={{ color: "#fff", fontSize: 30 }} />
              </Box>
            </Tooltip>
            <Typography sx={{ position: "absolute", bottom: -20, left: "50%", transform: "translateX(-50%)", fontSize: 9, fontWeight: 800, color: "#6366f1", whiteSpace: "nowrap", letterSpacing: "0.05em" }}>
              FEATURE TOUR
            </Typography>
          </Box>
        </Zoom>
      )}

      {/* Tour Panel */}
      {isOpen && (
        <Fade in>
          <Box
            sx={{
              position: "fixed",
              bottom: 100,
              left: "50%",
              transform: "translateX(-50%)",
              width: { xs: "96vw", sm: 700 },
              zIndex: 1400,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: "16px",
                overflow: "hidden",
                border: `1px solid ${current.color}30`,
                boxShadow: `0 12px 48px rgba(0,0,0,0.2), 0 0 0 1px ${current.color}20`,
                bgcolor: "#fff",
              }}
            >
              {/* Progress */}
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 3,
                  bgcolor: `${current.color}20`,
                  "& .MuiLinearProgress-bar": { bgcolor: current.color, transition: "width 0.5s ease" },
                }}
              />

              <Grid container sx={{ p: "20px 20px 16px" }}>
                {/* LEFT — avatar + minimap */}
                <Grid xs={12} sm="auto" sx={{ display: "flex", flexDirection: "column", alignItems: "center", pr: { sm: 2.5 }, mb: { xs: 2, sm: 0 }, minWidth: { sm: 110 } }}>
                  <BotAvatar color={current.color} isTalking={talking} />
                  <MiniMap steps={TOUR_STEPS} currentIdx={step} onJump={setStep} />
                </Grid>

                {/* RIGHT — content */}
                <Grid xs={12} sm sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {/* Title row */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ p: 0.9, borderRadius: "10px", bgcolor: `${current.color}15`, color: current.color, display: "flex" }}>
                        {React.cloneElement(current.icon, { sx: { fontSize: 20 } })}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1e293b", lineHeight: 1.3 }}>
                          {current.title}
                        </Typography>
                        <Chip
                          label={CATEGORY_META[current.category].label}
                          size="small"
                          sx={{
                            height: 17, fontSize: 9, fontWeight: 700, mt: 0.3,
                            bgcolor: `${CATEGORY_META[current.category].color}15`,
                            color: CATEGORY_META[current.category].color,
                            border: `1px solid ${CATEGORY_META[current.category].color}44`,
                          }}
                        />
                      </Box>
                    </Box>
                    <IconButton
                      size="small" onClick={close}
                      sx={{
                        bgcolor: "#fee2e2", color: "#dc2626", width: 28, height: 28,
                        "&:hover": { bgcolor: "#dc2626", color: "#fff" },
                        transition: "all 0.2s",
                      }}
                    >
                      <Close sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>

                  {/* Description */}
                  <Box
                    sx={{
                      p: 1.5, bgcolor: "#f8fafc", borderRadius: "10px",
                      border: "1px solid #e2e8f0", minHeight: 62,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#475569", lineHeight: 1.75 }}>
                      {displayed}
                      {typing && (
                        <Box component="span" sx={{
                          display: "inline-block", width: "2px", height: "13px",
                          bgcolor: current.color, ml: "1px", verticalAlign: "middle",
                          animation: "blink 0.7s step-end infinite",
                          "@keyframes blink": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0 } },
                        }} />
                      )}
                    </Typography>
                  </Box>

                  {/* Controls */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>
                      {step + 1} / {TOUR_STEPS.length}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.8, alignItems: "center" }}>
                      <Tooltip title="Restart">
                        <IconButton size="small" onClick={restart} sx={{ color: "#94a3b8", "&:hover": { color: current.color } }}>
                          <Replay sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={playing ? "Pause" : "Auto-Play"}>
                        <IconButton
                          size="small" onClick={() => setPlaying((p) => !p)}
                          sx={{
                            bgcolor: playing ? `${current.color}18` : "transparent",
                            color: current.color,
                            border: `1px solid ${current.color}44`,
                            borderRadius: "8px",
                            "&:hover": { bgcolor: `${current.color}28` },
                          }}
                        >
                          {playing ? <Pause sx={{ fontSize: 17 }} /> : <PlayArrow sx={{ fontSize: 17 }} />}
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small" onClick={prev} disabled={isFirst}
                        startIcon={<NavigateBefore />}
                        sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12, color: "#475569", "&:disabled": { opacity: 0.3 } }}
                      >
                        Back
                      </Button>
                      <Button
                        size="small"
                        onClick={isLast ? close : next}
                        endIcon={isLast ? <CheckCircle /> : <NavigateNext />}
                        variant="contained"
                        sx={{
                          borderRadius: "8px", textTransform: "none", fontSize: 12,
                          bgcolor: current.color, "&:hover": { bgcolor: `${current.color}dd` },
                          boxShadow: `0 2px 10px ${current.color}44`,
                        }}
                      >
                        {isLast ? "Done!" : "Next"}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Fade>
      )}
    </>
  );
}
